import React from 'react';
import { LogoIcon } from './Logo';
import { PhoneMockup, BrowserMockup } from './LandingMockups';

// 디자인 시스템: docs/design/AUTODEX_DESIGN.md (시안 D — Revolut 구조 하이브리드, 2026-07-28)
export var PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=kr.autodex.app';
export var APP_STORE_URL = 'https://apps.apple.com/kr/app/autodex/id6789510320';

var COLORS = {
  black: '#000000', elev: '#14171c', hairDark: 'rgba(255,255,255,0.12)',
  soft: '#f4f6f9', hairLight: '#e2e2e7',
  ink: '#191c1f', mute: '#505a63', onDarkMute: 'rgba(255,255,255,0.72)',
  brand: '#2563eb', navy: '#1B3A5C', sky: '#7fb2ff'
};

var ArrowRight = function(){return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>};
var CheckIcon = function(p){return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={p.c||COLORS.sky} strokeWidth="2.5"><path d="M5 13l4 4L19 7"></path></svg>};
var AppleLogo = function(p){return <svg viewBox="0 0 384 512" width="22" height="22" fill={p.c}><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>};
var PlayLogo = function(p){return <svg viewBox="0 0 512 512" width="22" height="22" fill={p.c}><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/></svg>};

// 스토어 배지 — 외부 이미지 의존 없이 인라인 SVG (DESIGN.md §5). 초대 가입 완료 화면(Register.js)도 같이 쓴다.
export var StoreBadge = function(p){
  var light = p.light;
  var fg = light ? COLORS.ink : '#000';
  return (
    <a href={p.href} className="lp-store" style={{display:'flex',alignItems:'center',gap:10,borderRadius:12,height:56,padding:'0 20px 0 16px',textDecoration:'none',background:light?COLORS.soft:'#fff',color:fg,border:light?'1px solid '+COLORS.hairLight:'none'}}>
      {p.kind==='apple' ? <AppleLogo c={fg}/> : <PlayLogo c={fg}/>}
      <span>
        <span style={{display:'block',fontSize:10,fontWeight:500,lineHeight:1.2,opacity:0.65}}>{p.kind==='apple'?'Download on the':'GET IT ON'}</span>
        <span style={{display:'block',fontSize:15,fontWeight:700,lineHeight:1.15}}>{p.kind==='apple'?'App Store':'Google Play'}</span>
      </span>
    </a>
  );
};

var btnBase = {display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,borderRadius:9999,height:48,padding:'0 28px',fontSize:16,fontWeight:600,textDecoration:'none',cursor:'pointer'};

export default function LandingPage() {
  var rootRef = React.useRef(null);

  React.useEffect(function(){
    var root = rootRef.current;
    if (!root) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var runCounter = function(el){
      var to = parseInt(el.getAttribute('data-to'), 10);
      if (reduced) { el.textContent = to; return; }
      var dur = 1100, t0 = performance.now();
      var tick = function(t){
        var p = Math.min((t - t0) / dur, 1);
        el.textContent = Math.round(to * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if (!e.isIntersecting) return;
        e.target.classList.add('in');
        e.target.querySelectorAll('.lp-cnt').forEach(runCounter);
        io.unobserve(e.target);
      });
    }, {threshold: 0.18});
    root.querySelectorAll('.lp-rv').forEach(function(el){ io.observe(el); });
    return function(){ io.disconnect(); };
  }, []);

  return (
    <div ref={rootRef} style={{fontFamily:"'Pretendard',-apple-system,'Apple SD Gothic Neo','Malgun Gothic',sans-serif",WebkitFontSmoothing:'antialiased',background:COLORS.black,color:'#fff',overflowX:'hidden'}}>
      <style>{`
        .lp-d-xxl{font-size:clamp(40px,6.6vw,92px);font-weight:600;line-height:1.05;letter-spacing:-0.03em;word-break:keep-all}
        .lp-d-lg{font-size:clamp(30px,4.2vw,52px);font-weight:600;line-height:1.08;letter-spacing:-0.02em;word-break:keep-all}
        .lp-sub{font-size:clamp(16px,1.7vw,19px);line-height:1.6}
        a.lp-btn:active{transform:scale(0.97)}
        a.lp-btn-white:hover{background:#dfe4ec!important}
        a.lp-btn-outline:hover{background:rgba(255,255,255,0.1)!important}
        a.lp-store{transition:transform 0.18s,box-shadow 0.18s}
        a.lp-store:hover{transform:translateY(-2px)}
        a.lp-weblink:hover{text-decoration:underline}
        .lp-card-hover{transition:transform 0.25s cubic-bezier(0.22,0.61,0.36,1),box-shadow 0.25s}
        .lp-card-hover:hover{transform:translateY(-5px)}
        .lp-pain-card:hover{box-shadow:0 16px 40px rgba(25,28,31,0.08)}
        .lp-seq{opacity:0;transform:translateY(24px);animation:lpRise 0.8s cubic-bezier(0.22,0.61,0.36,1) forwards}
        .lp-seq2{animation-delay:0.12s}.lp-seq3{animation-delay:0.24s}.lp-seq4{animation-delay:0.36s}.lp-seq5{animation-delay:0.48s}
        @keyframes lpRise{to{opacity:1;transform:none}}
        .lp-phone{animation:lpHeroRise 1.1s cubic-bezier(0.22,0.61,0.36,1) 0.45s both}
        @keyframes lpHeroRise{from{opacity:0;transform:translateY(90px)}to{opacity:1;transform:none}}
        @keyframes lpPing{75%,100%{transform:scale(2);opacity:0}}
        .lp-rv{opacity:0;transform:translateY(30px);transition:opacity 0.75s cubic-bezier(0.22,0.61,0.36,1),transform 0.75s cubic-bezier(0.22,0.61,0.36,1)}
        .lp-rv.in{opacity:1;transform:none}
        .lp-rv-d1{transition-delay:0.08s}.lp-rv-d2{transition-delay:0.16s}.lp-rv-d3{transition-delay:0.24s}
        .lp-num{font-variant-numeric:tabular-nums}
        @media(max-width:900px){
          .lp-pain-grid,.lp-feat-grid,.lp-step-grid,.lp-sec-grid,.lp-diff-grid{grid-template-columns:1fr!important}
          .lp-chip-grid{grid-template-columns:1fr 1fr!important}
          .lp-stat-grid{grid-template-columns:repeat(2,1fr)!important;row-gap:36px}
          .lp-nav-links{display:none!important}
          .lp-nav-login{display:none!important}
          .lp-section{padding:64px 0!important}
          .lp-dl-row{flex-direction:column;align-items:flex-start!important}
          .lp-web-phone{display:none!important}
          .lp-web-side{width:118px!important;padding:13px 8px!important}
          .lp-web-kpi{grid-template-columns:1fr 1fr!important}
          .lp-web-hide-sm{display:none!important}
          .lp-flow{gap:5px!important}
          .lp-flow-pill{font-size:11px!important;padding:5px 9px!important}
          .lp-flow-arrow{font-size:11px!important}
        }
        @media(prefers-reduced-motion:reduce){
          .lp-seq,.lp-phone,.lp-ping{animation:none;opacity:1;transform:none}
          .lp-rv{opacity:1;transform:none;transition:none}
        }
      `}</style>

      {/* 네비게이션 */}
      <nav style={{position:'fixed',top:0,width:'100%',background:'rgba(0,0,0,0.72)',backdropFilter:'blur(14px)',zIndex:50}}>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px',display:'flex',alignItems:'center',gap:28,height:64}}>
          <div style={{display:'flex',alignItems:'center',gap:9}}>
            <LogoIcon size={28} />
            <span style={{fontWeight:800,fontSize:18,letterSpacing:'-0.02em'}}>AUTODEX</span>
          </div>
          <div className="lp-nav-links" style={{display:'flex',gap:26,fontSize:14,fontWeight:500,flex:1,justifyContent:'center'}}>
            <a href="#features" style={{color:COLORS.onDarkMute,textDecoration:'none'}}>기능소개</a>
            <a href="#how-it-works" style={{color:COLORS.onDarkMute,textDecoration:'none'}}>이용방법</a>
          </div>
          <div style={{display:'flex',gap:12,alignItems:'center',marginLeft:'auto'}}>
            <a href="/login" className="lp-nav-login" style={{fontSize:14,fontWeight:600,color:COLORS.onDarkMute,textDecoration:'none'}}>로그인</a>
            <a href="/register" className="lp-btn lp-btn-white" style={Object.assign({},btnBase,{background:'#fff',color:'#000',height:40,padding:'0 20px',fontSize:14})}>무료로 시작하기</a>
          </div>
        </div>
      </nav>

      {/* 히어로 (블랙) */}
      <header style={{padding:'150px 0 120px',textAlign:'center',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',top:-160,left:'50%',transform:'translateX(-50%)',width:960,height:640,borderRadius:'50%',background:'radial-gradient(50% 50% at 50% 40%, rgba(37,99,235,0.20) 0%, rgba(37,99,235,0.06) 55%, transparent 75%)',filter:'blur(8px)',pointerEvents:'none'}}></div>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px',position:'relative'}}>
          <div className="lp-seq" style={{display:'inline-flex',alignItems:'center',gap:8,padding:'7px 16px',borderRadius:9999,background:'rgba(37,99,235,0.12)',border:'1px solid rgba(127,178,255,0.25)',color:COLORS.sky,fontSize:12,fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',marginBottom:28}}>
            <span style={{position:'relative',display:'flex',width:8,height:8}}>
              <span className="lp-ping" style={{position:'absolute',width:'100%',height:'100%',borderRadius:'50%',background:COLORS.sky,opacity:0.75,animation:'lpPing 1.5s cubic-bezier(0,0,0.2,1) infinite'}}></span>
              <span style={{width:8,height:8,borderRadius:'50%',background:COLORS.sky}}></span>
            </span>
            AI-Powered Export Automation
          </div>
          <h1 className="lp-d-xxl lp-seq lp-seq2" style={{marginBottom:24}}>중고차 수출,<br/><span style={{color:COLORS.sky}}>AI가 자동화</span>합니다</h1>
          <p className="lp-sub lp-seq lp-seq3" style={{color:COLORS.onDarkMute,maxWidth:'34em',margin:'0 auto 32px'}}>
            등록증 OCR부터 서류 자동생성, 세무처리, 선적까지.<br/>단 하나의 플랫폼에서 모든 수출 업무를 완벽하게 관리하세요.
          </p>
          <div className="lp-seq lp-seq4" style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap',marginBottom:20}}>
            <a href="/register" className="lp-btn lp-btn-white" style={Object.assign({},btnBase,{background:'#fff',color:'#000'})}>무료로 시작하기 <ArrowRight/></a>
            <a href="mailto:support@autodex.kr" className="lp-btn lp-btn-outline" style={Object.assign({},btnBase,{background:'transparent',color:'#fff',border:'1px solid #fff'})}>도입 문의하기</a>
          </div>
          <div className="lp-seq lp-seq5" style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
            <StoreBadge kind="apple" href={APP_STORE_URL} />
            <StoreBadge kind="play" href={PLAY_STORE_URL} />
          </div>
          <div className="lp-seq lp-seq5" style={{display:'flex',gap:8,justifyContent:'center',alignItems:'center',flexWrap:'wrap',marginTop:18,fontSize:14,color:COLORS.onDarkMute}}>
            <span>설치 없이 웹에서 바로 시작할 수 있어요</span>
            <a href="/register" className="lp-weblink" style={{color:COLORS.sky,fontWeight:600,textDecoration:'none'}}>웹으로 시작하기 →</a>
          </div>
        </div>
      </header>

      {/* 불편함 (화이트) */}
      <section className="lp-section" style={{background:'#fff',color:COLORS.ink,padding:'96px 0'}}>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px'}}>
          <div className="lp-rv" style={{maxWidth:660,marginBottom:52}}>
            <h2 className="lp-d-lg" style={{marginBottom:14}}>이런 불편함, 겪고 계시죠?</h2>
            <p className="lp-sub" style={{color:COLORS.mute}}>하루의 절반이 이런 일에 쓰이고 있진 않나요?</p>
          </div>
          <div className="lp-pain-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:18}}>
            {[{t:'수작업 입력의 늪',d:'등록증을 보면서 차대번호, 연식, 제원을 하나하나 옮겨 적는 시간. 하루에도 몇 번씩 반복하다 보면 실수까지 생깁니다.'},
              {t:'엑셀 관리의 한계',d:'버전이 꼬인 엑셀 파일, 담당자가 자리를 비우면 멈추는 업무. 어느 파일이 최신인지부터 찾아야 합니다.'},
              {t:'기한 누락 리스크',d:'수출이행기간을 달력에 적어두고 챙기다 하루만 놓쳐도 과태료입니다. 차가 늘수록 불안도 함께 늘어납니다.'}
            ].map(function(item,i){return (
              <div key={i} className={'lp-rv lp-card-hover lp-pain-card'+(i>0?' lp-rv-d'+i:'')} style={{background:'#fff',border:'1px solid '+COLORS.hairLight,borderRadius:20,padding:32}}>
                <h3 style={{fontSize:19,fontWeight:700,marginBottom:10}}>{item.t}</h3>
                <p style={{fontSize:14.5,color:COLORS.mute,lineHeight:1.55}}>{item.d}</p>
              </div>
            )})}
          </div>
        </div>
      </section>

      {/* 기능소개 (블랙) */}
      <section id="features" className="lp-section" style={{background:COLORS.black,padding:'96px 0'}}>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px'}}>
          <div className="lp-rv" style={{maxWidth:660,marginBottom:52}}>
            <span style={{color:COLORS.sky,fontWeight:700,letterSpacing:'0.05em',fontSize:14,textTransform:'uppercase',display:'block',marginBottom:12}}>Solutions</span>
            <h2 className="lp-d-lg" style={{marginBottom:14}}>AUTODEX가 완벽히 해결합니다</h2>
            <p className="lp-sub" style={{color:COLORS.onDarkMute}}>매일 반복하던 일들이 이렇게 줄어듭니다.</p>
          </div>
          <div className="lp-feat-grid" style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:18,marginBottom:18}}>
            {[{badge:'AI OCR',featured:true,t:'초고속 AI OCR',d:'등록증 사진 한 장이면 끝납니다. 차대번호, 연식, 제원, 소유자 정보가 몇 초 만에 그대로 입력됩니다. 더 이상 타이핑하지 않아도 됩니다.'},
              {badge:'DOCS',t:'원클릭 서류 생성',d:'차량 정보를 한 번만 넣으면 매매계약서, 인보이스, 패킹리스트가 클릭 한 번에 만들어집니다. 서류마다 같은 내용을 다시 쓸 일이 없습니다.'},
              {badge:'SMART UPLOAD',t:'AI 서류 자동 분류',d:'말소증, 면장, 구매확인서, 세금계산서 — 올리기만 하면 무슨 서류인지 알아서 구분하고, 해당 차량에 붙이고, 진행 상태까지 바꿔 둡니다.'},
              {badge:'ALERT',t:'스마트 기한 알림',d:'수출이행기간 D-day를 차량마다 자동으로 셉니다. 말소·선적 기한이 다가오면 앱 푸시와 이메일로 미리 알려드리니 과태료 걱정을 덜 수 있습니다.'}
            ].map(function(f,i){return (
              <div key={i} className={'lp-rv lp-card-hover'+(i%2===1?' lp-rv-d1':'')} style={{background:f.featured?COLORS.brand:COLORS.elev,borderRadius:20,padding:32}}>
                <span style={{display:'inline-block',background:f.featured?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.1)',borderRadius:9999,fontSize:11,fontWeight:700,padding:'4px 12px',marginBottom:18,color:f.featured?'#fff':COLORS.onDarkMute}}>{f.badge}</span>
                <h3 style={{fontSize:21,fontWeight:600,letterSpacing:'-0.01em',marginBottom:10}}>{f.t}</h3>
                <p style={{fontSize:15,color:f.featured?'rgba(255,255,255,0.85)':COLORS.onDarkMute,lineHeight:1.55}}>{f.d}</p>
              </div>
            )})}
          </div>
          {/* 차별점 — 말소 대행(주인공) + 경매장(보조) */}
          <div className="lp-diff-grid" style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:18,marginBottom:18}}>
            <div className="lp-rv lp-card-hover" style={{background:'#fff',color:COLORS.ink,borderRadius:20,padding:32}}>
              <span style={{display:'inline-block',background:COLORS.brand,color:'#fff',borderRadius:9999,fontSize:11,fontWeight:700,padding:'4px 12px',marginBottom:18}}>EXCLUSIVE</span>
              <h3 style={{fontSize:22,fontWeight:700,letterSpacing:'-0.01em',marginBottom:6}}>말소 대행</h3>
              <p style={{fontSize:16,fontWeight:700,color:COLORS.brand,letterSpacing:'-0.01em',marginBottom:10,wordBreak:'keep-all'}}>카톡으로 주고받던 말소, 클릭 한 번으로</p>
              <p style={{fontSize:15,color:COLORS.mute,lineHeight:1.6,marginBottom:20}}>신청하면 제휴 대행업체에 실시간으로 접수되고, 대행업체가 직접 방문해 차량을 수거합니다. 말소증이 올라오면 전산에 자동으로 반영되고, 업체에 알림이 발송됩니다. 이제 카톡으로 진행 상황을 물어볼 일이 없습니다.</p>
              <div className="lp-flow" style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:8}}>
                {[{s:'신청'},{s:'접수'},{s:'방문 수거·말소'},{s:'자동 반영·알림',hl:true}].map(function(p,i){return (
                  <React.Fragment key={i}>
                    {i>0 ? <span className="lp-flow-arrow" style={{color:COLORS.mute,fontSize:13,flexShrink:0}}>→</span> : null}
                    <span className="lp-flow-pill" style={{background:p.hl?'#e8f0fe':COLORS.soft,color:p.hl?COLORS.brand:COLORS.ink,borderRadius:9999,fontSize:12,fontWeight:p.hl?700:600,padding:'6px 12px',whiteSpace:'nowrap'}}>{p.s}</span>
                  </React.Fragment>
                )})}
              </div>
            </div>
            <div className="lp-rv lp-rv-d1 lp-card-hover" style={{background:COLORS.elev,borderRadius:20,padding:32}}>
              <span style={{display:'inline-block',background:'rgba(255,255,255,0.1)',color:COLORS.onDarkMute,borderRadius:9999,fontSize:11,fontWeight:700,padding:'4px 12px',marginBottom:18}}>AUCTION</span>
              <h3 style={{fontSize:21,fontWeight:600,letterSpacing:'-0.01em',marginBottom:10}}>등록한 차량, 경매로 판매까지</h3>
              <p style={{fontSize:15,color:COLORS.onDarkMute,lineHeight:1.55}}>ERP에 등록해 둔 차량을 그대로 해외 바이어 경매에 올릴 수 있습니다. 바이어 전용 앱에서 입찰을 받고, 낙찰되면 수출 절차로 자연스럽게 이어집니다.</p>
            </div>
          </div>
          <div className="lp-feat-grid" style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:18,marginBottom:56}}>
            {[{badge:'CUSTOMS',t:'선적까지 자동 확인',d:'수출신고 후에는 관세청 이행내역을 알아서 조회해 선적완료까지 처리합니다. 배가 떠났는지 일일이 확인하지 않아도 됩니다.'},
              {badge:'FINANCE',t:'실시간 손익 관리',d:'매입가, 수리비, 물류비까지 차량별로 모아 보여줍니다. 이 차에서 얼마가 남는지, 대시보드에서 바로 확인됩니다.'}
            ].map(function(f,i){return (
              <div key={i} className={'lp-rv lp-card-hover'+(i%2===1?' lp-rv-d1':'')} style={{background:f.featured?COLORS.brand:COLORS.elev,borderRadius:20,padding:32}}>
                <span style={{display:'inline-block',background:f.featured?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.1)',borderRadius:9999,fontSize:11,fontWeight:700,padding:'4px 12px',marginBottom:18,color:f.featured?'#fff':COLORS.onDarkMute}}>{f.badge}</span>
                <h3 style={{fontSize:21,fontWeight:600,letterSpacing:'-0.01em',marginBottom:10}}>{f.t}</h3>
                <p style={{fontSize:15,color:f.featured?'rgba(255,255,255,0.85)':COLORS.onDarkMute,lineHeight:1.55}}>{f.d}</p>
              </div>
            )})}
          </div>
          {/* 세부 기능 — 실제 탑재 기능만 나열 */}
          <div className="lp-rv" style={{background:COLORS.elev,borderRadius:20,padding:32}}>
            <h3 style={{fontSize:18,fontWeight:700,marginBottom:20}}>수출 실무의 전 과정을 커버합니다</h3>
            <div className="lp-chip-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px 20px'}}>
              {['수출신고필증(면장) OCR','세금계산서 자동 수취 (팝빌 연동)','부가세 환급·월별 장부 리포트',
                '바이어 입금·잔금 관리','웹·모바일 앱 실시간 동기화','현장 검차·매입 연동 (평가사 앱)',
                '다중 사업자·직원 계정 지원'
              ].map(function(c,i){return (
                <div key={i} style={{display:'flex',alignItems:'center',gap:8,fontSize:13.5,color:COLORS.onDarkMute}}>
                  <CheckIcon/> {c}
                </div>
              )})}
            </div>
          </div>
          {/* 실제 화면 미리보기 — 웹 관리자(주인공) + 앱(보조) */}
          <div className="lp-rv" style={{marginTop:64}}>
            <h3 style={{fontSize:20,fontWeight:700,marginBottom:8}}>한눈에 보는 AUTODEX</h3>
            <p style={{fontSize:15,color:COLORS.onDarkMute,marginBottom:28}}>사무실에서는 웹 관리자로, 현장에서는 모바일 앱으로 — 같은 데이터가 실시간으로 이어집니다.</p>
            <div style={{position:'relative',paddingBottom:24}}>
              <BrowserMockup/>
              <div className="lp-web-phone" style={{position:'absolute',right:0,bottom:-16,transform:'scale(0.58)',transformOrigin:'bottom right',pointerEvents:'none'}}>
                <PhoneMockup/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 이용방법 + 앱 다운로드 + 보안 (화이트) */}
      <section id="how-it-works" className="lp-section" style={{background:'#fff',color:COLORS.ink,padding:'96px 0'}}>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px'}}>
          <div className="lp-rv" style={{maxWidth:660,marginBottom:52}}>
            <h2 className="lp-d-lg" style={{marginBottom:14}}>도입은 단 3단계면 충분합니다</h2>
            <p className="lp-sub" style={{color:COLORS.mute}}>복잡한 세팅 없이 가입 즉시 사용할 수 있습니다.</p>
          </div>
          <div className="lp-step-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:18,marginBottom:56}}>
            {[{n:'1',t:'등록증 촬영/업로드',d:'스마트폰으로 찍거나 스캔한 차량 등록증을 올립니다.'},
              {n:'2',t:'AI가 데이터 추출',d:'차량 정보가 자동으로 읽혀 목록에 정리됩니다. 확인만 하면 됩니다.'},
              {n:'3',t:'수출 서류 즉시 발급',d:'매매계약서부터 인보이스까지, 필요한 서류를 바로 뽑아 공유합니다.'}
            ].map(function(s,i){return (
              <div key={i} className={'lp-rv'+(i>0?' lp-rv-d'+i:'')} style={{background:COLORS.soft,borderRadius:20,padding:30}}>
                <div style={{width:34,height:34,borderRadius:'50%',background:'#000',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:14,marginBottom:18}}>{s.n}</div>
                <h3 style={{fontSize:18,fontWeight:700,marginBottom:8}}>{s.t}</h3>
                <p style={{fontSize:14.5,color:COLORS.mute,lineHeight:1.55}}>{s.d}</p>
              </div>
            )})}
          </div>
          <div className="lp-rv lp-dl-row" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:24,flexWrap:'wrap',background:COLORS.soft,borderRadius:20,padding:32,marginBottom:96}}>
            <div>
              <h3 style={{fontSize:22,fontWeight:700,marginBottom:6}}>모바일에서도 그대로</h3>
              <p style={{fontSize:14.5,color:COLORS.mute}}>현장에서 등록증을 찍으면 사무실 PC에 바로 반영됩니다. 웹과 앱 어느 쪽에서든 같은 데이터로 일합니다.</p>
            </div>
            <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
              <StoreBadge kind="apple" href={APP_STORE_URL} light />
              <StoreBadge kind="play" href={PLAY_STORE_URL} light />
            </div>
          </div>
          {/* 보안/신뢰 — 실제 운영 중인 보안 체계만 기재 */}
          <div className="lp-rv" style={{maxWidth:660,marginBottom:52}}>
            <h2 className="lp-d-lg" style={{marginBottom:14}}>소중한 데이터, 안전하게 지킵니다</h2>
            <p className="lp-sub" style={{color:COLORS.mute}}>차량과 고객 정보는 회사의 자산입니다. 금융 서비스 수준의 보안 체계로 보관합니다.</p>
          </div>
          <div className="lp-sec-grid" style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:18}}>
            {[{t:'256bit AES 암호화',d:'주민등록번호 등 민감정보는 256bit AES로 암호화되어 저장되며, 통신 구간은 HTTPS로 보호됩니다.'},
              {t:'매일 자동 백업',d:'데이터베이스는 매일 새벽 자동 백업되며, 서류 원본은 Google Cloud 스토리지에 안전하게 보관됩니다.'},
              {t:'가입 승인제 운영',d:'모든 계정은 사업자 확인 등 검토를 거쳐 승인됩니다. 검증된 수출업체만 함께합니다.'},
              {t:'계정별 접근 권한',d:'관리자·직원·대행업체 등 역할별로 접근 범위를 분리하고, 모든 민감정보 열람을 기록합니다.'}
            ].map(function(s,i){return (
              <div key={i} className={'lp-rv lp-card-hover lp-pain-card'+(i%2===1?' lp-rv-d1':'')} style={{background:'#fff',border:'1px solid '+COLORS.hairLight,borderRadius:20,padding:32}}>
                <h3 style={{fontSize:18,fontWeight:700,marginBottom:10}}>{s.t}</h3>
                <p style={{fontSize:14.5,color:COLORS.mute,lineHeight:1.55}}>{s.d}</p>
              </div>
            )})}
          </div>
        </div>
      </section>

      {/* 통계 + CTA (블랙) */}
      <section className="lp-section" style={{background:COLORS.black,padding:'96px 0'}}>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px'}}>
          <div className="lp-rv lp-stat-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:18,textAlign:'center',marginBottom:96}}>
            {[{to:200,suffix:'+대',l:'누적 등록 차량'},{to:100,suffix:'+대',l:'수출 완료 차량'},{to:900,suffix:'+건',l:'누적 처리 서류'},{to:365,suffix:'일',l:'수출이행기간 자동 추적'}].map(function(s,i){return (
              <div key={i}>
                <div className="lp-num" style={{fontSize:'clamp(34px,4.6vw,56px)',fontWeight:600,letterSpacing:'-0.02em',lineHeight:1.0,marginBottom:8}}>
                  <span className="lp-cnt lp-num" data-to={s.to}>0</span>{s.suffix}
                </div>
                <div style={{fontSize:14,color:COLORS.onDarkMute}}>{s.l}</div>
              </div>
            )})}
          </div>
          <div className="lp-rv" style={{textAlign:'center'}}>
            <h2 className="lp-d-lg" style={{marginBottom:14}}>오늘부터 수출 업무를 자동화하세요</h2>
            <p style={{color:COLORS.onDarkMute,fontSize:17,marginBottom:30}}>모든 기능을 무료로 사용할 수 있습니다.</p>
            <a href="/register" className="lp-btn lp-btn-white" style={Object.assign({},btnBase,{background:'#fff',color:'#000'})}>무료로 시작하기</a>
          </div>
        </div>
      </section>

      {/* 푸터 (블랙) */}
      <footer id="contact" style={{background:COLORS.black,color:COLORS.onDarkMute,padding:'64px 0',fontSize:14,borderTop:'1px solid '+COLORS.hairDark}}>
        <div style={{maxWidth:1160,margin:'0 auto',padding:'0 24px',display:'flex',justifyContent:'space-between',gap:24,flexWrap:'wrap',alignItems:'center'}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:9,marginBottom:10}}>
              <LogoIcon size={24} />
              <span style={{fontWeight:800,fontSize:16,color:'#fff'}}>AUTODEX</span>
            </div>
            <p>© 2026 AUTODEX. All rights reserved.</p>
          </div>
          <div style={{display:'flex',gap:22}}>
            <a href="/terms" style={{color:COLORS.onDarkMute,textDecoration:'none'}}>이용약관</a>
            <a href="/privacy" style={{color:'#fff',textDecoration:'none',fontWeight:700}}>개인정보처리방침</a>
          </div>
          <a href="mailto:support@autodex.kr" style={{color:COLORS.onDarkMute,textDecoration:'none'}}>✉ support@autodex.kr</a>
        </div>
      </footer>
    </div>
  );
}
